import { StandardEvents } from '@shopify/events';

const pickerSelector = 'main[data-template="product"] .product-details .nacarbe-compact-picker, #quick-add-dialog .nacarbe-compact-picker';
const pendingRequests = new WeakMap();
const observedModals = new WeakSet();

function selectedFit(picker) {
  return picker.querySelector('[data-nc-option="horma"] input:checked');
}

function updateAudience(picker) {
  const fieldset = picker.querySelector('fieldset[data-nc-option="horma"]');
  if (!fieldset) return;
  const labels = Array.from(fieldset.querySelectorAll('label[data-nc-audience]'));
  const groups = [...new Set(labels.map(label => label.dataset.ncAudience))];
  const selected = selectedFit(picker)?.closest('label')?.dataset.ncAudience || groups[0];
  let audience = picker.querySelector('[data-nc-audience-bar]');
  if (!audience && groups.length > 1) {
    audience = document.createElement('div');
    audience.className = 'nc-product-audience';
    audience.dataset.ncAudienceBar = '';
    audience.setAttribute('role', 'group');
    audience.setAttribute('aria-label', picker.dataset.ncAudienceLabel);
    for (const key of ['women', 'men', 'kids', 'other']) {
      if (!groups.includes(key)) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.ncChooseAudience = key;
      button.textContent = picker.dataset['nc' + key[0].toUpperCase() + key.slice(1)];
      audience.append(button);
    }
    fieldset.before(audience);
  }
  for (const button of audience?.querySelectorAll('button') || []) {
    button.setAttribute('aria-pressed', String(button.dataset.ncChooseAudience === selected));
  }
  for (const label of labels) label.hidden = label.dataset.ncAudience !== selected;
  picker.dataset.ncEnhanced = 'true';
}

function updatePreview(picker, resource) {
  const preview = picker.querySelector('[data-nc-preview]');
  if (!preview) return;
  const selection = preview.querySelector('[data-nc-selection]');
  const fields = Array.from(picker.querySelectorAll('fieldset input:checked, .variant-option__select option:checked'));
  const fit = fields.find(input => input.dataset.optionName?.toLowerCase().trim() === 'horma');
  const rest = fields.filter(input => input !== fit);
  const text = [fit, ...rest].filter(Boolean).map(input => input.value).join(' · ');
  if (selection && text) selection.textContent = text;
  preview.removeAttribute('aria-busy');
  const status = preview.querySelector('[data-nc-selection-status]');
  if (resource === null) {
    if (selection) selection.textContent = preview.dataset.ncUnavailable;
    if (status) status.textContent = preview.dataset.ncUnavailable;
  } else if (status && resource) {
    status.textContent = text + ' · ' + preview.querySelector('.nc-product-preview__price').textContent;
  }
}

function updateSizes(picker) {
  const fit = selectedFit(picker);
  if (!fit?.dataset.ncCompatibleSizes) return;
  const sizes = JSON.parse(fit.dataset.ncCompatibleSizes);
  if (!sizes.length) return;
  for (const input of picker.querySelectorAll('[data-nc-option="talla"] input, [data-nc-option="size"] input')) {
    const label = input.closest('label');
    if (label) label.hidden = !sizes.includes(input.value);
  }
}

function enhance(root = document) {
  for (const picker of root.querySelectorAll(pickerSelector)) {
    updateAudience(picker);
    updateSizes(picker);
    updatePreview(picker);
  }
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-nc-choose-audience]');
  if (button) {
    const picker = button.closest(pickerSelector);
    if (!picker) return;
    const labels = Array.from(picker.querySelectorAll('label[data-nc-audience]'));
    const label = labels.find(item => item.dataset.ncAudience === button.dataset.ncChooseAudience);
    const input = label?.querySelector('input');
    if (input && !input.checked) {
      input.click();
      updateAudience(picker);
      updateSizes(picker);
    }
    return;
  }
  const link = event.target.closest('[data-nc-expand]');
  if (!link) return;
  const gallery = (link.closest('quick-add-dialog') || link.closest('.shopify-section'))?.querySelector('media-gallery');
  if (typeof gallery?.zoom === 'function' && gallery.querySelector('zoom-dialog')) {
    event.preventDefault();
    gallery.zoom(0, event);
  }
});

document.addEventListener(StandardEvents.productSelect, event => {
  if (!(event.target instanceof Element)) return;
  const picker = event.target.closest(pickerSelector);
  if (!picker || !event.promise) return;
  const scope = picker.closest('quick-add-dialog') || document;
  const sequence = (pendingRequests.get(scope) || 0) + 1;
  pendingRequests.set(scope, sequence);
  const preview = picker.querySelector('[data-nc-preview]');
  preview?.setAttribute('aria-busy', 'true');
  const status = preview?.querySelector('[data-nc-selection-status]');
  if (status) status.textContent = preview.dataset.ncUpdating;
  event.promise.then(({ detail }) => {
    if (sequence !== pendingRequests.get(scope)) return;
    enhance();
    const current = scope.querySelector(pickerSelector);
    if (current) updatePreview(current, detail?.resource ?? null);
  }).catch(error => {
    if (sequence !== pendingRequests.get(scope) || error?.name === 'AbortError') return;
    scope.querySelector(pickerSelector)?.querySelector('[data-nc-preview]')?.removeAttribute('aria-busy');
  });
});

function watchQuickAdd() {
  for (const content of document.querySelectorAll('#quick-add-modal-content')) {
    if (observedModals.has(content)) continue;
    new MutationObserver(() => enhance()).observe(content, { childList: true });
    observedModals.add(content);
  }
}

function initialize() {
  enhance();
  watchQuickAdd();
}

document.addEventListener('shopify:section:load', initialize);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
